import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, PagingSidePanelType, SidePanel } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { appBannersId } from 'components/app/AppBanners'
import { SidePanelHeaderTemplateTwo } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateTwo'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { Country } from 'types/authentication/Profile'
import { BeneficiaryDetails, BeneficiaryEntityType } from 'types/beneficiaries'
import { highlightRowBasedOnId } from 'utils/table/table'
import { titleCase } from 'utils/utils'
import { BeneficiarySidePanelBody } from './BeneficiarySidePanelBody'

export type BeneficiarySummaryProps = {
  isOpen: boolean
  beneficiaryDetails: BeneficiaryDetails
  pageBeneficiaries: PagingSidePanelType
  onClose: () => void
}

export const BeneficiarySummary = ({
  isOpen,
  beneficiaryDetails,
  pageBeneficiaries,
  onClose,
}: BeneficiarySummaryProps) => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)
  const getCountryNameByCode = (code: string, countries: Country[]): string => {
    const foundCountry = countries?.find(country => country.code === code)
    return foundCountry ? foundCountry.name : code
  }

  const aliasLabel = beneficiaryDetails?.alias ? `(${beneficiaryDetails.alias})` : ''
  const primaryLabelIndividual = `${beneficiaryDetails?.firstName} ${beneficiaryDetails?.lastName} ${aliasLabel}`
  const primaryLabelBusiness = `${beneficiaryDetails?.businessName} ${aliasLabel}`
  const getPrimaryLabel = () => {
    if (beneficiaryDetails?.entityType === BeneficiaryEntityType.BUSINESS) {
      return primaryLabelBusiness
    }
    return primaryLabelIndividual
  }

  const secondaryLabel = `${titleCase(beneficiaryDetails?.entityType)} ${t('beneficiary')}`

  const iconUrl = beneficiaryDetails?.currency
    ? `${process.env.ICONS_URL}${beneficiaryDetails.currency?.toLowerCase()}.svg`
    : process.env.ICONS_URL

  const accountNumber = beneficiaryDetails?.fiat?.accountNumber

  return (
    <SidePanel
      id="side-panel-beneficiary-summary"
      isOpen={isOpen}
      paging={pageBeneficiaries}
      onClose={onClose}
      activeRowId={beneficiaryDetails?.reference}
      onActiveRowChange={highlightRowBasedOnId}
      topOffset={bannerSpacing}
      header={
        <SidePanelHeaderTemplateTwo
          heading={{
            iconUrls: [iconUrl],
            primaryLabel: (
              <Box className="md:flex w-full">
                <h2 className="text-foreground-prominent text-h2">{getPrimaryLabel()}</h2>
              </Box>
            ),
            secondaryLabel: (
              <p className="text-body-regular text-foreground-prominent">{secondaryLabel}</p>
            ),
          }}
          bottomContent={{
            leftLabel: t('accountNumber'),
            leftDescription: beneficiaryDetails?.fiat?.accountNumber && (
              <p className="text-foreground-prominent text-body-regular">
                {accountNumber.length > 30
                  ? ellipseMiddleText(accountNumber, 10, 10)
                  : accountNumber}
              </p>
            ),
            rightLabel: t('beneficiaryPaymentReference'),
            rightDescription: beneficiaryDetails?.beneficiaryPaymentReference && (
              <p className=" mb-1 flex-wrap break-words text-foreground-prominent text-body-regular">
                {beneficiaryDetails?.beneficiaryPaymentReference}
              </p>
            ),
          }}
        />
      }
      body={
        <Box tag="section" width="full" background="standard">
          <BeneficiarySidePanelBody
            beneficiaryDetails={beneficiaryDetails}
            getCountryNameByCode={getCountryNameByCode}
          />
        </Box>
      }
    />
  )
}
